import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import { Box, Button, Section, Tag, Text } from '@factorialco/deprecated-design-system'
import type { AllFeatureIds } from 'generated/featureIds.generated'
import {
  BulkUpdateBookkeeperDocument,
  BulkUpdateBookkeeperMutation,
  CyclePageDocument,
} from 'generated/resources/graphql'
import type { CyclePageQuery } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { checkFeatures } from 'modules/core/hooks/useFeatures'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import { EmployeeSelectorV2MultiSelectState } from 'modules/employees/components/EmployeeSelectorV2/types'

type Bookkeeper = CyclePageQuery['bookkeepersManagement']['bookkeepersConnection']['nodes'][number]

const NoBookkeepers = () => {
  return (
    <Text.Normal size="300" color="grey800" weight="medium">
      {i18n.t('app_store_v2.apps.payroll_bundle.settings.no_bookkeeper_selected')}
    </Text.Normal>
  )
}

interface BookkeepersListProps {
  bookkeepers: Bookkeeper[]
  removeInactiveBookkeeper: (userId: string) => void
}

const BookkeepersList = ({ bookkeepers, removeInactiveBookkeeper }: BookkeepersListProps) => {
  return bookkeepers.map((bookkeeper, index, collection) => {
    const lastItemIndex = collection.length - 1
    const isLast = index === lastItemIndex

    return (
      <Box
        flexDirection="row"
        justifyContent="spaceBetween"
        alignItems="center"
        border={
          !isLast && {
            bottom: {
              color: 'grey400',
              width: 's1',
              style: 'solid',
            },
          }
        }
        padding="s16"
        key={bookkeeper.id}
      >
        <Text.Normal size="300" color="grey1000" weight="normal">
          {bookkeeper?.access?.fullName}
        </Text.Normal>

        {bookkeeper?.access?.isDeactivated && (
          <Box flexDirection="row" gap="s8" alignItems="center">
            <>
              <Tag size="small" leftItem="circle" showCircle type={Tag.Types.grey}>
                {i18n.t(
                  'app_store_v2.apps.payroll_bundle.settings.professional_settings.status.inactive'
                )}
              </Tag>
            </>

            <Button
              type="secondary"
              onClick={() => removeInactiveBookkeeper(bookkeeper.access?.id ?? '')}
              iconName={'delete'}
            ></Button>
          </Box>
        )}
      </Box>
    )
  })
}

const FEATURE_IDS: AllFeatureIds[] = ['factorial_professionals']

const ProfessionalSettingsContent = () => {
  const { id: cycleId } = useParams()
  const {
    company: { id: companyId },
  } = useCurrentSession()
  const { data, loading, error } = useQuery(CyclePageDocument, {
    variables: {
      companyId,
      cycleId: String(cycleId),
      featureIds: FEATURE_IDS,
    },
  })

  const { has_factorial_professionals: hasProfessionals } = checkFeatures(
    ['factorial_professionals'],
    data?.features?.featuresConnection.nodes
  )

  const bookkeepers = data?.bookkeepersManagement.bookkeepersConnection.nodes ?? []
  const fetchedBKsIds = bookkeepers
    .map((bookkeeper) => bookkeeper?.access?.id)
    .filter((id): id is string => Boolean(id))

  const [bookkeeperIds, setBookkeeperIds] = useState<string[]>([])
  const [bookkeepersModal, setBookkeepersModal] = useState(false)
  const [isSubmitting, setSubmitting] = useState(false)
  const hasInitializedRef = useRef(false)
  const previousFetchedIdsRef = useRef<string>('')

  // Sync bookkeeperIds with fetched data on initial load and after refetches
  useEffect(() => {
    if (!loading && data) {
      const fetchedIdsString = JSON.stringify([...fetchedBKsIds].sort())

      // Initialize on first load
      if (!hasInitializedRef.current) {
        if (fetchedBKsIds.length > 0) {
          setBookkeeperIds(fetchedBKsIds)
          hasInitializedRef.current = true
          previousFetchedIdsRef.current = fetchedIdsString
        }
      } else {
        // After initialization, sync if fetched data has changed (e.g., after mutation refetch)
        // This ensures UI stays in sync with server data
        if (previousFetchedIdsRef.current !== fetchedIdsString) {
          setBookkeeperIds((currentIds) => {
            const currentIdsString = JSON.stringify([...currentIds].sort())
            // Only update if different to avoid unnecessary re-renders
            if (currentIdsString !== fetchedIdsString) {
              return fetchedBKsIds
            }
            return currentIds
          })
          previousFetchedIdsRef.current = fetchedIdsString
        }
      }
    }
  }, [loading, data, fetchedBKsIds])

  const [bulkUpdateBookkeepers, { loading: loadingCreate }] = useMutation(
    BulkUpdateBookkeeperDocument,
    {
      extractorFn: (data: BulkUpdateBookkeeperMutation) =>
        data.bookkeepersManagementMutations.bulkUpdateBookkeepersmanagementBookkeeper,
    }
  )

  const updateBookkeepers = async (newBookkeepersIds: string[]) => {
    setSubmitting(true)
    const handled = handleResult(
      await bulkUpdateBookkeepers({
        variables: {
          bookkeeperAccessIds: newBookkeepersIds,
          companyId,
          cycleId,
        },
        refetchQueries: [CyclePageDocument],
        awaitRefetchQueries: true,
      }),
      (errors) => {
        setSubmitting(false)
        app.setFlash({
          type: 'wrong',
          message: Object.values(errors).join(', ') ?? i18n.t('common.save_error'),
        })
      }
    )

    if (handled.success) {
      setBookkeepersModal(false)
      setSubmitting(false)
      setBookkeeperIds(newBookkeepersIds)
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    }
  }

  const removeInactiveBookkeeper = (userId: string) => {
    const activeBookkeepers = bookkeeperIds.filter((id) => id !== userId)
    updateBookkeepers(activeBookkeepers)
  }

  const submitLabel = bookkeeperIds.length
    ? i18n.t('app_store_v2.apps.payroll_bundle.install_modal.bookkeeper.submit')
    : i18n.t('app_store_v2.apps.payroll_bundle.install_modal.bookkeeper.submit_nobookkeeper')

  if (!cycleId) return null
  if (loading) return <Loading />
  if (error) throw new Error('Error fetching data')
  if (!hasProfessionals) return null

  return (
    <Section
      title={i18n.t(
        'app_store_v2.apps.payroll_bundle.settings.professional_settings.section_title'
      )}
      illustration="briefcase"
      description={i18n.t(
        'app_store_v2.apps.payroll_bundle.settings.professional_settings.section_description'
      )}
    >
      <Box gap="s24">
        <Box
          borderRadius={{
            all: 'abs016',
          }}
          border={
            bookkeepers.length
              ? {
                  all: {
                    color: 'grey400',
                    width: 's1',
                    style: 'solid',
                  },
                }
              : {}
          }
        >
          {bookkeepers.length ? (
            <BookkeepersList
              bookkeepers={bookkeepers}
              removeInactiveBookkeeper={removeInactiveBookkeeper}
            />
          ) : (
            <NoBookkeepers />
          )}
          {bookkeepersModal && (
            <Modal
              illustration="user-tie"
              title={i18n.t('app_store_v2.apps.payroll_bundle.install_modal.bookkeeper.title')}
              description={i18n.t(
                'app_store_v2.apps.payroll_bundle.install_modal.bookkeeper.modal_description'
              )}
              onClose={() => setBookkeepersModal(false)}
              onSubmit={async () => {
                await updateBookkeepers(bookkeeperIds)
              }}
              footer={
                <>
                  <Button
                    type="secondary"
                    onClick={() => {
                      setSubmitting(false)
                      setBookkeepersModal(false)
                    }}
                  >
                    {i18n.t('common.cancel')}
                  </Button>
                  <Button disabled={isSubmitting} submit>
                    {submitLabel}
                  </Button>
                </>
              }
            >
              <Box gap="s24">
                <EmployeeSelectorV2
                  label={i18n.t('app_store_v2.apps.payroll_bundle.install_modal.bookkeeper.label')}
                  value={bookkeeperIds}
                  onChange={(state: EmployeeSelectorV2MultiSelectState) => {
                    if (!state) return
                    if (state.type === 'manual') setBookkeeperIds(state.ids)
                  }}
                  options={{
                    hideSearchIcon: true,
                    disableSelectAll: true,
                  }}
                  multiple
                  useAccessId
                  affiliations={['employee', 'external']}
                />
              </Box>
            </Modal>
          )}
        </Box>

        <Button
          type="secondary"
          onClick={() => setBookkeepersModal(true)}
          iconName={bookkeepers.length ? 'users' : 'add'}
        >
          {bookkeepers.length
            ? i18n.t(
                'app_store_v2.apps.payroll_bundle.settings.professional_settings.button.manage'
              )
            : i18n.t('app_store_v2.apps.payroll_bundle.settings.professional_settings.button.add')}
        </Button>
      </Box>
    </Section>
  )
}

const ProfessionalSettings = () => <ProfessionalSettingsContent />

export default observer(ProfessionalSettings)
